/**
 * Legal entity types and the registration number each one actually issues.
 *
 * Onboarding used to demand a CIPC number in the form YYYY/NNNNNN/NN, so a trust, an NPO, a
 * medical practice, a partnership and a sole proprietor could not be captured at all. Staff
 * either turned the client away or typed something that would pass the regex, which put
 * unusable data in the KYC field of a registered credit provider.
 *
 * This must stay in step with `LegalEntityType.java` in maliyone-customer-api. The backend is
 * authoritative - these rules exist so the business sees the error while typing rather than
 * after submitting.
 */

export type LegalEntityTypeCode =
  | 'PTY_LTD'
  | 'INCORPORATED'
  | 'PUBLIC_COMPANY'
  | 'CLOSE_CORPORATION'
  | 'NPC'
  | 'CO_OPERATIVE'
  | 'TRUST'
  | 'NPO'
  | 'MEDICAL_PRACTICE'
  | 'PARTNERSHIP'
  | 'SOLE_PROPRIETOR'
  | 'OTHER'
  | 'CUSTOMER_ID';

export interface LegalEntityTypeDefinition {
  code: LegalEntityTypeCode;
  /** Shown in the dropdown. */
  label: string;
  /** What the registration-number field is called for this form. */
  registrationLabel: string;
  /** A valid example, used as placeholder and in the error message. */
  example: string | null;
  /** Format the number must match. null means any non-blank value is accepted. */
  pattern: RegExp | null;
  /** Short note shown under the field, when the form needs explaining. */
  hint?: string;
  /** True when the entity is identified by a person rather than its own registration. */
  usesOwnerIdentity?: boolean;
}

export const LEGAL_ENTITY_TYPES: LegalEntityTypeDefinition[] = [
  {
    code: 'PTY_LTD',
    label: 'Private Company (Pty) Ltd',
    registrationLabel: 'Company registration number',
    example: '2021/359709/07',
    pattern: /^(18|19|20)\d{2}\/\d{6}\/07$/,
  },
  {
    code: 'INCORPORATED',
    label: 'Personal Liability Company (Inc)',
    registrationLabel: 'Company registration number',
    example: '2015/123456/21',
    pattern: /^(18|19|20)\d{2}\/\d{6}\/21$/,
    hint: 'Used by attorneys, auditors and similar professional firms.',
  },
  {
    code: 'PUBLIC_COMPANY',
    label: 'Public Company (Ltd)',
    registrationLabel: 'Company registration number',
    example: '1998/123456/06',
    pattern: /^(18|19|20)\d{2}\/\d{6}\/06$/,
  },
  {
    code: 'CLOSE_CORPORATION',
    label: 'Close Corporation (CC)',
    registrationLabel: 'CC registration number',
    example: '2008/123456/23',
    pattern: /^(CK)?(18|19|20)\d{2}\/\d{6}\/23$/,
    hint: 'No new CCs since 2011, but existing ones still trade. Older numbers start with CK.',
  },
  {
    code: 'NPC',
    label: 'Non-Profit Company (NPC)',
    registrationLabel: 'Company registration number',
    example: '2019/123456/08',
    pattern: /^(18|19|20)\d{2}\/\d{6}\/08$/,
  },
  {
    code: 'CO_OPERATIVE',
    label: 'Co-operative',
    registrationLabel: 'Co-operative registration number',
    example: '2017/123456/24',
    pattern: /^(18|19|20)\d{2}\/\d{6}\/24$/,
  },
  {
    code: 'TRUST',
    label: 'Trust',
    registrationLabel: 'Trust registration number (IT number)',
    example: 'IT1234/2020',
    pattern: /^IT\s?\d{1,6}\/(18|19|20)\d{2}$/i,
    hint: 'Issued by the Master of the High Court, not CIPC. Also capture the letters of authority.',
  },
  {
    code: 'NPO',
    label: 'Non-Profit Organisation (NPO)',
    registrationLabel: 'NPO registration number',
    example: '123-456 NPO',
    pattern: /^\d{3}-\d{3}\s?NPO$/i,
    hint: 'Issued by the Department of Social Development.',
  },
  {
    code: 'MEDICAL_PRACTICE',
    label: 'Medical / Healthcare Practice',
    registrationLabel: 'Practice number (BHF/HPCSA)',
    example: '0123456',
    pattern: /^\d{7}$/,
    hint: 'If the practice is also registered as an Inc or (Pty) Ltd, select that instead and capture the CIPC number.',
  },
  {
    code: 'PARTNERSHIP',
    label: 'Partnership',
    registrationLabel: 'Partnership identifier',
    example: null,
    pattern: null,
    usesOwnerIdentity: true,
    hint: 'Partnerships are not registered in South Africa. Capture each partner as a director with their ID number.',
  },
  {
    code: 'SOLE_PROPRIETOR',
    label: 'Sole Proprietor',
    registrationLabel: "Owner's SA ID number",
    example: '8001015009087',
    pattern: /^\d{13}$/,
    usesOwnerIdentity: true,
    hint: 'A sole proprietor has no separate legal existence - the owner is the business.',
  },
  {
    code: 'OTHER',
    label: 'Other',
    registrationLabel: 'Registration / identifying number',
    example: null,
    pattern: null,
    hint: 'Use only when no option above fits. The number is stored as typed and not checked.',
  },
  {
    // Historical clients the business holds no registration number for (e.g. Agile Motion). Must stay in step
    // with LegalEntityType.CUSTOMER_ID in maliyone-customer-api: the customer number IS the registration number.
    code: 'CUSTOMER_ID',
    label: 'Customer ID (no registration number)',
    registrationLabel: 'Customer ID',
    example: null,
    pattern: null,
    hint: 'For historical clients with no registration number on file. The customer number is used and filled in automatically - nothing to type. Cannot be checked at the credit bureau.',
  },
];

/** True when the legal form is identified by the customer number, so there is no registration number to capture. */
export const usesCustomerId = (code?: string | null): boolean => code === 'CUSTOMER_ID';

const BY_CODE = new Map<string, LegalEntityTypeDefinition>(
  LEGAL_ENTITY_TYPES.map((definition) => [definition.code, definition])
);

export const getLegalEntityType = (
  code?: string | null
): LegalEntityTypeDefinition | undefined => (code ? BY_CODE.get(code) : undefined);

/**
 * Trim and collapse whitespace, including around the slashes.
 *
 * Spaces here are not cosmetic. "2021 / 359709 / 07" and "2021/359709/07" were stored as two
 * different companies, which is how one borrower ended up with two customer records - their
 * whole borrowing history on one, and a R900 000 application on the other showing no history.
 */
export const normalizeRegistrationNumber = (value?: string): string =>
  (value ?? '').trim().replace(/\s+/g, ' ').replace(/\s*\/\s*/g, '/');

/**
 * Field label for the registration number, given the chosen legal form.
 */
export const registrationLabelFor = (code?: string | null): string =>
  getLegalEntityType(code)?.registrationLabel ?? 'Registration number';

/**
 * Placeholder for the registration number field.
 */
export const registrationPlaceholderFor = (code?: string | null): string => {
  const example = getLegalEntityType(code)?.example;
  return example ? `e.g. ${example}` : 'Registration or identifying number';
};

/**
 * Validate a registration number against the chosen legal form.
 *
 * Returns true when valid, or the message to show. Used directly as a react-hook-form
 * `validate` function.
 */
export const validateRegistrationNumber = (
  value: string | undefined,
  legalEntityTypeCode?: string | null
): true | string => {
  const normalized = normalizeRegistrationNumber(value);

  // Customer ID: the customer service sets the number itself, so a blank field is correct.
  if (usesCustomerId(legalEntityTypeCode)) {
    return true;
  }

  if (!normalized) {
    return `${registrationLabelFor(legalEntityTypeCode)} is required`;
  }

  const definition = getLegalEntityType(legalEntityTypeCode);

  // No legal form chosen yet. Accept anything rather than showing a CIPC error for a trust the
  // user is about to select - the form asks for the type first, and the backend is authoritative.
  if (!definition) {
    return true;
  }

  if (!definition.pattern) {
    return true;
  }

  // The label is NOT lowercased. It carries acronyms - IT number, BHF/HPCSA, NPO - and
  // lowercasing rendered "not a valid trust registration number (it number)", which reads as a
  // typo on a form a client-facing team uses all day.
  return definition.pattern.test(normalized)
    ? true
    : `Expected a ${definition.registrationLabel} for a ${definition.label}` +
        (definition.example ? `, e.g. ${definition.example}` : '') + '.';
};
